import styled from 'styled-components';
import {
  color,
  compose,
  ColorProps,
  typography,
  TypographyProps,
  display,
  DisplayProps,
  space,
  SpaceProps,
  layout,
  LayoutProps,
  ResponsiveValue,
  system
} from 'styled-system';
import * as CSS from 'csstype';

const WEIGHTS = {
  LIGHT: 300,
  REGULAR: 400,
  SEMI_BOLD: 600,
  BOLD: 700,
  EXTRA_BOLD: 800
};

// Common style utility props.
export type BaseStyleProps = ColorProps &
  TypographyProps &
  DisplayProps &
  SpaceProps &
  TextTransformProp &
  LayoutProps;

export interface TextTransformProp {
  textTransform?: ResponsiveValue<CSS.TextTransformProperty>;
}

const textTransform = system({
  textTransform: {
    property: 'textTransform'
  }
});

const typographyStyleProps = compose(
  space,
  color,
  typography,
  display,
  textTransform,
  layout
);

export const Regular = styled.p<BaseStyleProps>`
  margin: 0;
  font-weight: ${WEIGHTS.REGULAR};
  ${typographyStyleProps}
`;

export const Heading1 = styled.h1<BaseStyleProps>`
  margin-top: 24px;
  margin-bottom: 24px;
  font-weight: ${WEIGHTS.LIGHT};
  ${typographyStyleProps}
`;

export const Heading2 = styled.h2<BaseStyleProps>`
  margin-top: 12px;
  margin-bottom: 12px;
  font-weight: ${WEIGHTS.LIGHT};
  ${typographyStyleProps}
`;

export const Heading3 = styled.h3<BaseStyleProps>`
  margin-top: 8px;
  margin-bottom: 8px;
  font-weight: ${WEIGHTS.REGULAR};
  ${typographyStyleProps}
`;

export const Heading4 = styled.h4<BaseStyleProps>`
  margin-top: 4px;
  margin-bottom: 4px;
  font-weight: ${WEIGHTS.REGULAR};
  ${typographyStyleProps}
`;

export const Heading5 = styled.h5<BaseStyleProps>`
  margin-top: 4px;
  margin-bottom: 4px;
  font-weight: ${WEIGHTS.REGULAR};
  ${typographyStyleProps}
`;

export const Heading6 = styled.h6<BaseStyleProps>`
  margin-top: 4px;
  margin-bottom: 4px;
  font-weight: ${WEIGHTS.BOLD};
  ${typographyStyleProps}
`;

export const PageTitle = styled.h4<BaseStyleProps>`
  margin-top: 4px;
  margin-bottom: 4px;
  font-weight: ${WEIGHTS.EXTRA_BOLD};
  ${typographyStyleProps}
`;

export const Body1 = styled.p<BaseStyleProps>`
  margin-top: 16px;
  margin-bottom: 16px;
  ${typographyStyleProps}
`;

export const Body2 = styled.p<BaseStyleProps>`
  margin-top: 12px;
  margin-bottom: 12px;
  ${typographyStyleProps}
`;

export const Body3 = styled.p<BaseStyleProps>`
  margin-top: 12px;
  margin-bottom: 12px;
  ${typographyStyleProps}
`;

export const Button = styled.span<BaseStyleProps>`
  text-transform: uppercase;
  font-weight: ${WEIGHTS.BOLD};
  ${typographyStyleProps}
`;

export const ButtonSmall = styled.span<BaseStyleProps>`
  text-transform: uppercase;
  ${typographyStyleProps}
`;

export const Subtitle1 = styled.span<BaseStyleProps>`
  margin-top: 1px;
  margin-bottom: 1px;
  font-weight: ${WEIGHTS.EXTRA_BOLD};
  ${typographyStyleProps}
`;
export const Subtitle2 = styled.span<BaseStyleProps>`
  margin-top: 1px;
  margin-bottom: 1px;
  font-weight: ${WEIGHTS.BOLD};
  ${typographyStyleProps}
`;

export const Subtitle3 = styled.span<BaseStyleProps>`
  margin-top: 1px;
  margin-bottom: 1px;
  font-weight: ${WEIGHTS.SEMI_BOLD};
  ${typographyStyleProps}
`;

export const Caption = styled.span<BaseStyleProps>`
  margin-top: 1px;
  margin-bottom: 1px;
  font-weight: ${WEIGHTS.BOLD};
  ${typographyStyleProps}
`;

export const Overline = styled.span<BaseStyleProps>`
  margin-top: 1px;
  margin-bottom: 1px;
  text-transform: uppercase;
  font-weight: ${WEIGHTS.SEMI_BOLD};
  ${typographyStyleProps}
`;

export const Label = styled.label<BaseStyleProps>`
  ${typographyStyleProps}
`;

export const Hint = styled.span<BaseStyleProps>`
  display: block;
  font-style: italic;
  ${typographyStyleProps}
`;

export const Feedback = styled.span<BaseStyleProps>`
  display: block;
  font-style: italic;
  ${typographyStyleProps}
`;

export const Span = styled.span<BaseStyleProps>`
  margin: 0;
  font-weight: ${WEIGHTS.REGULAR};
  ${typographyStyleProps}
`;
